import { useState, useEffect, useCallback } from "react";

// "Cheesecakes" tab for the admin dashboard. Self-contained (inline styles).
// Every call goes to /api/cheesecake with the admin password header.
const SESSION_KEY = "gg_admin_auth";

async function api(body) {
  let pw = "";
  try { pw = sessionStorage.getItem(SESSION_KEY) || ""; } catch {}
  const res = await fetch("/api/cheesecake", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-password": pw },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    window.location.reload();
    throw new Error("Session expired. Please sign in again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Something went wrong.");
  return data;
}

// Whole dollars show without cents ($50); anything else keeps cents ($12.50).
const money = (n) => {
  const x = Number(n);
  return Number.isInteger(x) ? `$${x}` : `$${x.toFixed(2)}`;
};
const dateLabel = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

const card = { background: "#fff", border: "1px solid #E0DAD0", padding: 16, marginBottom: 12 };
const btn = { padding: "8px 14px", border: "1px solid #1E1C18", background: "#1E1C18", color: "#fff", cursor: "pointer", fontSize: 13 };
const btnLight = { ...btn, background: "#fff", color: "#1E1C18" };
const input = { padding: "6px 8px", border: "1px solid #C8D5C0", fontSize: 13 };
const h3 = { margin: "20px 0 10px", fontSize: 15 };

function RequestRow({ r, onChanged, setMsg }) {
  const [date, setDate] = useState(r.pickup_date);
  const [busy, setBusy] = useState(false);
  const decide = r.status === "requested" || r.status === "card_failed";

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { setMsg({ error: e.message }); } finally { setBusy(false); onChanged(); }
  };

  const approve = () => {
    if (!window.confirm(`Approve and charge ${r.customer_name}'s ${r.card_brand || "card"} ending ${r.card_last4} for ${money(r.total)}?`)) return;
    run(async () => {
      const d = await api({ action: "admin-approve", id: r.id, pickupDate: date !== r.pickup_date ? date : undefined });
      setMsg(d.warning ? { error: d.warning } : { ok: `Charged ${money(r.total)} and emailed ${r.customer_name}.` });
    });
  };
  const decline = () => {
    const reason = window.prompt("Optional note to the customer (leave blank for none):", "");
    if (reason === null) return;
    run(async () => {
      const d = await api({ action: "admin-decline", id: r.id, reason });
      setMsg(d.warning ? { error: d.warning } : { ok: `Declined. ${r.customer_name} was emailed and not charged.` });
    });
  };
  const markApproved = () => {
    if (!window.confirm("Only do this after you've confirmed in Square that the card WAS charged. Continue?")) return;
    run(async () => { await api({ action: "admin-mark-approved", id: r.id }); setMsg({ ok: "Marked approved and emailed the customer." }); });
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <strong>{r.customer_name}</strong>
        <span>{money(r.total)} · {r.quantity} cake{r.quantity === 1 ? "" : "s"}</span>
      </div>
      <div style={{ fontSize: 13, margin: "6px 0" }}>
        {r.email} · {r.phone}<br />
        {(r.items || []).map((i) => `${i.name} × ${i.quantity}`).join(", ")}<br />
        Requested pickup: <strong>{dateLabel(r.pickup_date)}</strong> · Card: {r.card_brand || "Card"} ending {r.card_last4}
      </div>
      {r.note && <div style={{ fontSize: 13, background: "#F6F2EA", padding: 8, margin: "6px 0" }}>Note: {r.note}</div>}
      {r.charge_error && <div style={{ fontSize: 13, color: "#9B4444", margin: "6px 0" }}>Charge problem: {r.charge_error}</div>}
      {r.decline_reason && <div style={{ fontSize: 13, opacity: 0.7 }}>Your note to customer: {r.decline_reason}</div>}
      {decide && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <label style={{ fontSize: 12 }}>Pickup date&nbsp;
            <input style={input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <button style={btn} disabled={busy} onClick={approve}>{busy ? "Working…" : `Approve & charge ${money(r.total)}`}</button>
          <button style={btnLight} disabled={busy} onClick={decline}>Decline</button>
        </div>
      )}
      {r.status === "charge_error" && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>We couldn't tell whether the card was charged. Check Square first, then choose:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={btn} disabled={busy} onClick={markApproved}>It WAS charged: mark approved</button>
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>Received {new Date(r.created_at).toLocaleString()}</div>
    </div>
  );
}

function Section({ title, rows, empty, onChanged, setMsg }) {
  return (
    <>
      <h3 style={h3}>{title} ({rows.length})</h3>
      {rows.length === 0 && empty && <div style={{ fontSize: 13, opacity: 0.6 }}>{empty}</div>}
      {rows.map((r) => <RequestRow key={r.id} r={r} onChanged={onChanged} setMsg={setMsg} />)}
    </>
  );
}

export default function CheesecakeAdmin() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [settings, setSettings] = useState({ open: false, minDays: 5, maxActive: 8 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    return api({ action: "admin-list" })
      .then((d) => { setData(d); setSettings(d.settings); setErr(null); })
      .catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api({ action: "admin-settings", open: settings.open, minDays: Number(settings.minDays), maxActive: Number(settings.maxActive) });
      setMsg({ ok: "Settings saved." });
      await load();
    } catch (e) { setMsg({ error: e.message }); } finally { setSaving(false); }
  };

  if (err) return <div style={{ padding: 24 }}>Couldn't load cheesecake requests: {err}</div>;
  if (!data) return <div style={{ padding: 24 }}>Loading…</div>;

  const by = (...s) => data.requests.filter((r) => s.includes(r.status));
  const waiting = by("requested", "card_failed");
  const attention = by("charge_error", "charging");
  const approved = by("approved");
  const declined = by("declined");

  return (
    <div style={{ padding: "16px 24px", maxWidth: 900 }}>
      {msg && (
        <div style={{ ...card, borderColor: msg.error ? "#9B4444" : "#8C9E82", color: msg.error ? "#9B4444" : "#1E1C18" }}>
          {msg.error || msg.ok} <button style={{ ...btnLight, marginLeft: 8, padding: "2px 8px" }} onClick={() => setMsg(null)}>Dismiss</button>
        </div>
      )}

      <div style={card}>
        <strong>Request form</strong>
        <div style={{ margin: "10px 0", display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
          <label><input type="checkbox" checked={settings.open} onChange={(e) => setSettings((s) => ({ ...s, open: e.target.checked }))} /> Accepting requests</label>
          <label>Earliest pickup (days from today) <input style={{ ...input, width: 60 }} type="number" min="0" max="60" value={settings.minDays} onChange={(e) => setSettings((s) => ({ ...s, minDays: e.target.value }))} /></label>
          <label>Max cheesecakes on the books <input style={{ ...input, width: 60 }} type="number" min="0" max="200" value={settings.maxActive} onChange={(e) => setSettings((s) => ({ ...s, maxActive: e.target.value }))} /></label>
          <button style={btn} disabled={saving} onClick={saveSettings}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          {data.active} cheesecake{data.active === 1 ? "" : "s"} currently on the books (waiting or confirmed, pickup date today or later). The public form is {data.settings.open ? "OPEN" : "CLOSED"}. Page: grindgather.com/cheesecakes
        </div>
      </div>

      <Section title="Waiting for your decision" rows={waiting} empty="Nothing waiting." onChanged={load} setMsg={setMsg} />
      {attention.length > 0 && <Section title="Needs your attention" rows={attention} onChanged={load} setMsg={setMsg} />}
      <Section title="Confirmed" rows={approved} empty="None yet." onChanged={load} setMsg={setMsg} />
      <details style={{ marginTop: 20 }}>
        <summary style={{ cursor: "pointer", fontSize: 15, fontWeight: 600, padding: "6px 0" }}>Declined ({declined.length})</summary>
        <div style={{ marginTop: 10 }}>
          {declined.length === 0 && <div style={{ fontSize: 13, opacity: 0.6 }}>None.</div>}
          {declined.map((r) => <RequestRow key={r.id} r={r} onChanged={load} setMsg={setMsg} />)}
        </div>
      </details>
    </div>
  );
}
